import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { loadAnalytics, revokeAnalytics } from '../lib/consent-analytics'

const CONSENT_STORAGE_VERSION = 2
const GA_ID: string | undefined = import.meta.env.VITE_GOOGLE_ANALYTICS_ID

// The 8 locales apps/marketing actually builds pages for (ca/va/eu/ja/zh
// retired 2026-08-15 — see apps/marketing/public/_redirects). Mirrors
// apps/web/functions/[[path]].ts's SUPPORTED_LOCALES.
const SUPPORTED_MARKETING_LOCALES = new Set(['en', 'es', 'gl', 'de', 'fr', 'it', 'nl', 'pt'])

// eu-compliance-reviewer 2026-09-11 (verdict on B-new-887, commit 1eb132dc6):
// i18n.language can be a full BCP-47 tag (en-US, es-ES) or a retired locale
// (ca, va, eu) — using it directly to build the cookie-policy URL can 404
// on the marketing app. Take the base language subtag and validate it
// against the locales the marketing app actually builds; fall back to en.
export function resolveCookiePolicyLocale(language: string | null | undefined): string {
  const base = (language || '').toLowerCase().split('-')[0]
  return SUPPORTED_MARKETING_LOCALES.has(base) ? base : 'en'
}

interface ConsentState {
  analytics: boolean
  functional: boolean
  advertising: boolean
  timestamp: string
  version: number
}

interface KlaroManager {
  acceptAll: () => void
  declineAll: () => void
  updateConsent: (serviceName: string, enabled: boolean) => void
  saveAndApplyConsents: () => void
  getConsent: (serviceName: string) => boolean
}

function getKlaroManager(): KlaroManager | null {
  if (typeof window !== 'undefined' && (window as any).klaro) {
    return (window as any).klaro.getManager((window as any).klaroConfig)
  }
  return null
}

function readStoredConsent(): ConsentState | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.split('; ').find(c => c.startsWith('xeito-consent='))
  if (!match) return null
  try {
    const decoded = decodeURIComponent(match.split('=')[1])
    const parsed = JSON.parse(decoded)
    // v1 records had {analytics, adsense, saved} with no version — treat as stale
    if (parsed.version !== CONSENT_STORAGE_VERSION) return null
    return parsed as ConsentState
  } catch {
    return null
  }
}

function hasConsentCookie(): boolean {
  if (typeof document === 'undefined') return false
  return readStoredConsent() !== null
}

function writeConsentCookie(state: {
  analytics: boolean
  functional: boolean
  advertising: boolean
}) {
  if (typeof document === 'undefined') return
  const payload: ConsentState = {
    ...state,
    timestamp: new Date().toISOString(),
    version: CONSENT_STORAGE_VERSION,
  }
  const value = encodeURIComponent(JSON.stringify(payload))
  const expires = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toUTCString()
  document.cookie = `xeito-consent=${value}; expires=${expires}; path=/; SameSite=Lax`
}

export function CookieConsent() {
  const { t, i18n } = useTranslation()
  const [visible, setVisible] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false)
  const [functionalEnabled, setFunctionalEnabled] = useState(false)
  const [klaroReady, setKlaroReady] = useState(false)

  useEffect(() => {
    // B-new-2259: banner visibility must not depend on Klaro (the web app never
    // loads klaro.js). Show it whenever there is no valid v2 consent cookie.
    const checkKlaroReady = () => {
      if (typeof window !== 'undefined' && (window as any).klaro && (window as any).klaroConfig) {
        setKlaroReady(true)
      }
    }

    const initAndCheck = () => {
      if ((window as any).klaro && (window as any).klaroConfig) {
        ;(window as any).klaro.setup((window as any).klaroConfig)
      }
      checkKlaroReady()
    }
    if (!hasConsentCookie()) {
      setVisible(true)
    }
    initAndCheck()
    window.addEventListener('load', initAndCheck)

    return () => window.removeEventListener('load', initAndCheck)
  }, [])

  // Expose openModal globally for Footer button
  useEffect(() => {
    ;(window as any).openCookieSettings = () => {
      const stored = readStoredConsent()
      if (stored) {
        setAnalyticsEnabled(stored.analytics)
        setFunctionalEnabled(stored.functional)
      } else if (klaroReady) {
        const manager = getKlaroManager()
        if (manager) {
          setAnalyticsEnabled(manager.getConsent('google-analytics') || false)
        }
      }
      setModalOpen(true)
    }
    return () => {
      delete (window as any).openCookieSettings
    }
  }, [klaroReady])

  // B-new-2259: the web app has no advertising control (ads never run on
  // app.xeito.ai), so advertising is always recorded and sent as false.
  function applyKlaroConsents(analytics: boolean, advertising: boolean) {
    try {
      const manager = getKlaroManager()
      if (manager) {
        manager.updateConsent('google-analytics', analytics)
        manager.updateConsent('google-adsense', advertising)
        manager.saveAndApplyConsents()
      }
    } catch (e) {
      console.warn('[CookieConsent] Klaro update failed:', e)
    }
  }

  function acceptAll() {
    applyKlaroConsents(true, false)
    writeConsentCookie({ analytics: true, functional: true, advertising: false })
    setAnalyticsEnabled(true)
    setFunctionalEnabled(true)
    setVisible(false)
    setModalOpen(false)
    loadAnalytics(GA_ID)
  }

  function rejectAll() {
    applyKlaroConsents(false, false)
    writeConsentCookie({ analytics: false, functional: false, advertising: false })
    setAnalyticsEnabled(false)
    setFunctionalEnabled(false)
    setVisible(false)
    setModalOpen(false)
    revokeAnalytics(GA_ID)
  }

  function savePreferences() {
    applyKlaroConsents(analyticsEnabled, false)
    writeConsentCookie({
      analytics: analyticsEnabled,
      functional: functionalEnabled,
      advertising: false,
    })
    setVisible(false)
    setModalOpen(false)
    if (analyticsEnabled) loadAnalytics(GA_ID)
    else revokeAnalytics(GA_ID)
  }

  function openModal() {
    const stored = readStoredConsent()
    if (stored) {
      setAnalyticsEnabled(stored.analytics)
      setFunctionalEnabled(stored.functional)
    } else if (klaroReady) {
      const manager = getKlaroManager()
      if (manager) {
        setAnalyticsEnabled(manager.getConsent('google-analytics') || false)
      }
    }
    setModalOpen(true)
  }

  // Prefer resolvedLanguage (i18next's post-fallback-negotiation value) over
  // the raw i18n.language, which can be a full BCP-47 tag or unresolved.
  const locale = resolveCookiePolicyLocale(i18n.resolvedLanguage ?? i18n.language)
  // B-new-887: cookie-policy redirects to the marketing app at the edge
  // (operator decision 2026-09-11) — link straight there.
  const cookiePolicyHref = `${import.meta.env.VITE_MARKETING_URL || 'https://xeito.ai'}/${locale}/cookie-policy/`

  return (
    <>
      {/* Banner */}
      {visible && (
        <div
          role="dialog"
          aria-label={t('cookies.title')}
          aria-live="polite"
          className="fixed bottom-0 left-0 right-0 z-50 border-t-2 border-[#00f0ff]/50 bg-[#0a0a0f]/95 backdrop-blur-md"
        >
          <div className="max-w-7xl mx-auto px-4 py-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex-1 min-w-0">
              <p className="font-mono text-sm font-bold text-[#00f0ff] mb-1">
                <span className="text-[#39ff14]">&gt;</span> {t('cookies.title')}
              </p>
              <p className="font-mono text-xs text-[#a0a0b0] leading-relaxed">
                {t('cookies.description')}{' '}
                <a
                  href={cookiePolicyHref}
                  className="text-[#00f0ff] hover:text-[#39ff14] underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('cookies.learnMore')}
                </a>
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
              <button
                onClick={rejectAll}
                className="px-5 py-2.5 font-mono text-xs font-bold uppercase tracking-wider border-2 border-[#a0a0b0] text-[#a0a0b0] hover:border-[#ff00ff] hover:text-[#ff00ff] transition-colors"
              >
                {t('cookies.rejectAll')}
              </button>
              <button
                onClick={openModal}
                className="inline-flex items-center gap-2 px-5 py-2.5 font-mono text-xs font-bold uppercase tracking-wider border-2 border-[#00f0ff]/50 text-[#00f0ff] hover:border-[#00f0ff] transition-colors"
                aria-label={t('cookies.customize')}
              >
                <span className="text-base leading-none">⚙</span>
                <span>{t('cookies.customize')}</span>
              </button>
              <button
                onClick={acceptAll}
                className="px-5 py-2.5 font-mono text-xs font-bold uppercase tracking-wider border-2 border-[#39ff14] text-[#39ff14] hover:bg-[#39ff14] hover:text-[#0a0a0f] transition-colors"
              >
                {t('cookies.acceptAll')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Settings modal */}
      {modalOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={t('cookies.settingsTitle')}
          className="fixed inset-0 z-50 flex items-center justify-center bg-[#0a0a0f]/80 backdrop-blur-sm p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setModalOpen(false)
          }}
        >
          <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto border-2 border-[#00f0ff]/50 bg-[#0a0a0f] p-8 font-mono">
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-[#1f1f25]">
              <h2 className="text-lg font-bold text-[#00f0ff]">
                <span className="text-[#39ff14]">&gt;</span> {t('cookies.settingsTitle')}
              </h2>
              <button
                onClick={() => setModalOpen(false)}
                className="text-[#a0a0b0] hover:text-[#ff00ff] transition-colors text-xl"
                aria-label={t('cookies.close')}
              >
                ✕
              </button>
            </div>

            {/* Essential — locked */}
            <div className="mb-4 p-4 border border-[#1f1f25] bg-[#0f0f15]">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-bold text-[#39ff14]">{t('cookies.essential')}</span>
                <span className="text-xs text-[#39ff14] border border-[#39ff14]/50 px-2 py-0.5">
                  ON
                </span>
              </div>
              <p className="text-xs text-[#a0a0b0] leading-relaxed">
                {t('cookies.essentialDescription')}
              </p>
            </div>

            {/* Analytics */}
            <ToggleRow
              label={t('cookies.analytics')}
              description={t('cookies.analyticsDescription')}
              enabled={analyticsEnabled}
              onToggle={() => setAnalyticsEnabled(!analyticsEnabled)}
              color="#00f0ff"
            />

            {/* Functional */}
            <ToggleRow
              label={t('cookies.functional')}
              description={t('cookies.functionalDescription')}
              enabled={functionalEnabled}
              onToggle={() => setFunctionalEnabled(!functionalEnabled)}
              color="#ffa500"
            />

            <div className="flex flex-col gap-2 mt-6">
              <button
                onClick={acceptAll}
                className="w-full py-3 font-bold text-sm uppercase tracking-wider border-2 border-[#39ff14] text-[#39ff14] hover:bg-[#39ff14] hover:text-[#0a0a0f] transition-colors"
              >
                {t('cookies.acceptAll')}
              </button>
              <button
                onClick={savePreferences}
                className="w-full py-3 font-bold text-sm uppercase tracking-wider border-2 border-[#00f0ff] text-[#00f0ff] hover:bg-[#00f0ff] hover:text-[#0a0a0f] transition-colors"
              >
                {t('cookies.savePreferences')}
              </button>
              <button
                onClick={rejectAll}
                className="w-full py-3 font-bold text-sm uppercase tracking-wider border-2 border-[#a0a0b0] text-[#a0a0b0] hover:border-[#ff00ff] hover:text-[#ff00ff] transition-colors"
              >
                {t('cookies.rejectAll')}
              </button>
            </div>

            <div className="mt-4 text-center text-xs text-[#a0a0b0]">
              <a
                href={cookiePolicyHref}
                className="hover:text-[#00f0ff] transition-colors underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('cookies.learnMore')}
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

interface ToggleRowProps {
  label: string
  description: string
  enabled: boolean
  onToggle: () => void
  color: string
}

function ToggleRow({ label, description, enabled, onToggle, color }: ToggleRowProps) {
  return (
    <div className="mb-4 p-4 border border-[#1f1f25] bg-[#0f0f15]">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-bold" style={{ color }}>
          {label}
        </span>
        <button
          role="switch"
          aria-checked={enabled}
          aria-label={label}
          onClick={onToggle}
          className={`relative w-10 h-5 border transition-colors ${
            enabled ? 'bg-opacity-20 border-opacity-100' : 'bg-[#1f1f25] border-[#2f2f35]'
          }`}
          style={enabled ? { backgroundColor: `${color}33`, borderColor: color } : undefined}
        >
          <span
            className="absolute top-0.5 left-0.5 w-4 h-4 transition-all duration-200"
            style={{
              transform: enabled ? 'translateX(20px)' : 'translateX(0)',
              backgroundColor: enabled ? color : '#a0a0b0',
            }}
          />
        </button>
      </div>
      <p className="text-xs text-[#a0a0b0] leading-relaxed">{description}</p>
    </div>
  )
}
